import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, CalendarPlus, BarChart3, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * Sticky mobile bottom navigation for authenticated users.
 * Rendered globally from App.js alongside UserMenu.
 *
 * Visible: only on mobile (sm:hidden) for authenticated users on authed pages.
 * Hidden on: public pages, auth pages, landing, and full-screen stat tracker
 * decks (/stats/game/*, /stats/flow/*) where it would collide with the UI.
 */
const HIDE_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/share/',
  '/stats/leaderboard/',
  '/team/',
  '/join',
  '/blog',
  '/terms',
  '/privacy',
  '/pricing',
  '/officials/join/',
  '/officials/respond/',
];

function shouldHide(pathname) {
  if (pathname === '/') return true;
  // Full-screen stat tracker — collides with sticky action bars.
  if (/^\/stats\/(game|flow)\//.test(pathname)) return true;
  return HIDE_PREFIXES.some(p => {
    const norm = p.replace(/\/$/, '');
    return pathname === norm || pathname.startsWith(norm + '/');
  });
}

const TABS = [
  { label: 'Home',     icon: Home,         path: '/my-schedules', testid: 'mobile-nav-home',     match: (p) => p === '/my-schedules' },
  { label: 'Schedule', icon: CalendarPlus, path: '/create',       testid: 'mobile-nav-schedule', match: (p) => p === '/create' || p.startsWith('/schedule/') || p.startsWith('/import') },
  { label: 'Stats',    icon: BarChart3,    path: '/stats',        testid: 'mobile-nav-stats',    match: (p) => p === '/stats' || p.startsWith('/stats/') },
  { label: 'Account',  icon: User,         path: '/settings',     testid: 'mobile-nav-account',  match: (p) => p === '/settings' },
];

/**
 * Returns true when the mobile bottom nav should be visible for `pathname`
 * (shared with App.js so we can conditionally pad the container).
 */
export function isMobileNavVisiblePath(pathname) {
  return !shouldHide(pathname);
}

export default function MobileBottomNav() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAuthenticated || !user) return null;
  if (shouldHide(location.pathname)) return null;

  return (
    <nav
      className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-2px_12px_rgba(0,0,0,0.06)] print:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="grid grid-cols-4">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = tab.match(location.pathname);
          return (
            <button
              key={tab.path}
              type="button"
              onClick={() => navigate(tab.path)}
              data-testid={tab.testid}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 min-h-[56px] transition-colors ${
                active
                  ? 'text-[#00247D]'
                  : 'text-slate-500 hover:text-[#00247D] active:text-[#00247D]'
              }`}
            >
              <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
              <span className={`text-[11px] leading-tight ${active ? 'font-semibold' : 'font-medium'}`}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
